import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { Field, useFormState } from 'react-final-form';
import { Alert, Button, FieldError, Modal, ModalBody, ModalFooter, ModalHeader } from '@/ui';
import { formatDate } from '../../../utils/conversionFunctions';
import CommandK from '../../../components/CommandK/CommandK/CommandK';
import AuditModalButton from '../../../components/AuditModal/AuditModal';
import DetailHeader from '../../../components/DetailHeader/DetailHeader';
import ToggleActiveModal from './ToggleActiveModal';

const ToggleIcon = ({ isOpen, onClick, label }) => (
  <IconButton size='small' onClick={onClick} aria-label={label}>
    {isOpen ? <KeyboardDoubleArrowUp fontSize='small' /> : <KeyboardDoubleArrowDown fontSize='small' />}
  </IconButton>
);

const ModalComponent = ({ isOpen, setIsOpen, tone, children, handleConfirm, confirmText }) => (
  <Modal isOpen={isOpen} toggle={() => setIsOpen(false)} tone={tone}>
    <ModalHeader toggle={() => setIsOpen(false)}>{confirmText} Offer Block</ModalHeader>
    <ModalBody>{children}</ModalBody>
    <ModalFooter>
      <Button color={tone} onClick={handleConfirm}>
        {confirmText}
      </Button>
      <Button color='secondary' onClick={() => setIsOpen(false)}>
        Cancel
      </Button>
    </ModalFooter>
  </Modal>
);

const Navbar = ({
  leftAside,
  setLeftAside,
  shouldErrorRender,
  publishedSequence,
  rightAside,
  setRightAside,
  handleSubmit,
  setIsCopy,
}) => {
  const [isPublishedModalOpen, setIsPublishedModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const { offerBlockId } = useParams();
  // The block record the form was initialised with. Its name is edited in the left panel, so the
  // title here follows the field live; its id and active flag feed the ribbon and the toggle.
  const { values } = useFormState({ subscription: { values: true } });
  const offer = values?.offer || {};

  return (
    // The same header every other detail page has — title row with the actions, then the
    // Active toggle first on the row below — squared off and flush so it reads as this
    // full-screen editor's toolbar rather than a card floating above it.
    <DetailHeader
      isActive={Boolean(offer.isActive)}
      title={offer.name || 'New Offer Block'}
      subtitle={
        offer.id ? (
          <>
            <em>Offer Block ID: </em>
            {offer.id}
          </>
        ) : undefined
      }
      leading={
        <>
          <Button component={Link} to='/campaigns/offerblocks' color='link' startIcon={<ChevronLeft />}>
            Back to Zuul
          </Button>
          <ToggleIcon isOpen={!leftAside} onClick={setLeftAside} label='Toggle the offer list' />
        </>
      }
      actions={
        <>
          <AuditModalButton />
          {offerBlockId !== 'create' && (
            <Button color='success' onClick={() => setIsCopyModalOpen(true)}>
              Copy Offer Block
            </Button>
          )}
          <Button color='info' onClick={() => setIsPublishedModalOpen(true)}>
            Publish Offer Block
          </Button>
          <ToggleIcon isOpen={!rightAside} onClick={setRightAside} label='Toggle the properties panel' />
          <CommandK hide />
        </>
      }
      // No block to toggle until it has been published once.
      toggle={
        offer.id ? (
          <ToggleActiveModal
            framed
            isActive={Boolean(offer.isActive)}
            modalType='offerBlock'
            content={offer}
            endpoint={`offers/active/${offer.id}`}
          />
        ) : undefined
      }
      sx={{ mb: 0, borderRadius: 0, borderTop: 0, borderLeft: 0, borderRight: 0, boxShadow: 'none', flex: '0 0 auto' }}
    >
      <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
        {shouldErrorRender ? (
          <Alert color='danger' sx={{ py: 0 }}>
            One or more of the fields you entered are invalid.
          </Alert>
        ) : publishedSequence.publishedAt ? (
          <Typography noWrap>
            {'Last Published at ' +
              formatDate(publishedSequence.publishedAt) +
              ' by: ' +
              publishedSequence?.zuulUser?.realname}
          </Typography>
        ) : null}
      </Box>
      <ModalComponent
        isOpen={isPublishedModalOpen}
        setIsOpen={setIsPublishedModalOpen}
        tone='info'
        handleConfirm={() => {
          handleSubmit();
          setIsPublishedModalOpen(false);
        }}
        confirmText='Publish'
      >
        <Typography>Are you sure you want to publish this Offer Block?</Typography>
      </ModalComponent>
      <ModalComponent
        isOpen={isCopyModalOpen}
        setIsOpen={setIsCopyModalOpen}
        tone='warning'
        handleConfirm={() => {
          setIsCopy(true);
          handleSubmit();
          setIsCopyModalOpen(false);
        }}
        confirmText='Copy'
      >
        <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start', mb: 2 }}>
          <WarningAmber sx={{ fontSize: '3rem', color: 'warning.main', flex: '0 0 auto' }} />
          <Typography>
            Any changes made without saving will be copied to the new Offer Block but not reflected in the current
            Offer Block.
          </Typography>
        </Stack>
        <Field required name='name' validate={(value) => (!value ? 'Please add an Offer Block name' : undefined)}>
          {({ input, meta: { touched, error } }) => (
            <>
              <TextField
                {...input}
                id='offerBlockCopyName'
                label='Offer Block Name'
                placeholder='Offer Block Name'
                type='text'
                error={Boolean(touched && error)}
              />
              <FieldError error={error} touched={touched} />
            </>
          )}
        </Field>
      </ModalComponent>
    </DetailHeader>
  );
};

export default Navbar;
